import { Form, FormField, Input, ModalDialog, Select, Textarea } from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { AccessRule, AccessRuleStatus, AccessTarget } from "../../http/types/accessControl";
import { useAccessControlApi } from "../../http/useAccessControl";

type AccessRuleFormBody = {
  email: string | null;
  status: AccessRuleStatus;
  reason: string | null;
};

type AccessRuleModalProps = {
  // the app or module the rule is of
  target: AccessTarget;
  rule?: AccessRule;
  // a new rule for this email only, e.g. from the user's page
  email?: string;
  onClose: () => void;
};

const AccessRuleModal: FC<AccessRuleModalProps> = ({ target, rule, email, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const accessControlApi = useAccessControlApi();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusOptions: { value: AccessRuleStatus; label: string }[] = [
    { value: "allowed", label: i18n.translation.accessControl.rules.statusAllowed },
    { value: "blocked", label: i18n.translation.accessControl.rules.statusBlocked },
  ];

  const defaultValues: AccessRuleFormBody = {
    email: rule?.email ?? email ?? null,
    status: rule?.status ?? "blocked",
    reason: rule?.reason ?? null,
  };

  const onSubmit = () => async (formData: AccessRuleFormBody) => {
    if (!formData.email) {
      return;
    }

    setIsSubmitting(true);
    try {
      await accessControlApi.addRule(target, {
        email: formData.email,
        status: formData.status,
        reason: formData.reason ?? undefined,
      });
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={rule ? i18n.translation.common.edit : i18n.translation.accessControl.rules.createNew}
    >
      <Form<AccessRuleFormBody>
        submitText={i18n.translation.common.save}
        disabled={isSubmitting}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        {({ register, formState: { errors }, control }) => (
          <FormFieldsContainer>
            <FormField label={i18n.translation.accessControl.rules.email} error={errors.email}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={!!rule || !!email || isSubmitting}
                  {...register("email", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            <Controller
              name="status"
              control={control}
              rules={{ required: true }}
              defaultValue={defaultValues.status}
              render={({ field: { onChange, value, ref, ...fieldProps }, fieldState }) => (
                <FormField label={i18n.translation.accessControl.rules.status} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select
                      {...fieldProps}
                      inputId={labelId}
                      options={statusOptions}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      value={statusOptions.find((s) => s.value === value) ?? null}
                      onChange={(option: { value: AccessRuleStatus; label: string } | null) =>
                        onChange(option ? option.value : null)
                      }
                      isDisabled={isSubmitting}
                      isInvalid={fieldState.invalid}
                    />
                  )}
                </FormField>
              )}
            />

            <FormField label={i18n.translation.accessControl.rules.reason} isOptional>
              {({ labelId }) => <Textarea id={labelId} disabled={isSubmitting} {...register("reason")} />}
            </FormField>
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AccessRuleModal;
